import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { ArrowLeft, Wallet as WalletIcon, Plus, ArrowUpRight, ArrowDownLeft, CreditCard } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Wallet({ user, token }) {
  const navigate = useNavigate();
  const [wallet, setWallet] = useState({ balance: 0 });
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRechargeDialog, setShowRechargeDialog] = useState(false);
  const [rechargeAmount, setRechargeAmount] = useState('');

  useEffect(() => {
    fetchWalletData();
  }, []);

  const fetchWalletData = async () => {
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const walletRes = await axios.get(`${API}/wallet`, config);
      setWallet(walletRes.data);

      const transactionsRes = await axios.get(`${API}/wallet/transactions`, config);
      setTransactions(transactionsRes.data);
    } catch (error) {
      toast.error('Error al cargar billetera');
    } finally {
      setLoading(false);
    }
  };

  const handleRecharge = async () => {
    const amount = parseFloat(rechargeAmount);
    if (!amount || amount <= 0) {
      toast.error('Ingresa un monto válido');
      return;
    }

    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.post(`${API}/wallet/recharge`, null, { ...config, params: { amount } });
      
      toast.success(`Recarga exitosa de $${amount.toLocaleString()}`);
      setShowRechargeDialog(false);
      setRechargeAmount('');
      fetchWalletData();
    } catch (error) {
      toast.error('Error al recargar billetera');
    }
  };

  const getTransactionIcon = (type) => {
    switch (type) {
      case 'recharge':
        return <ArrowDownLeft className="h-5 w-5 text-green-500" />;
      case 'payment':
        return <ArrowUpRight className="h-5 w-5 text-red-500" />;
      default:
        return <WalletIcon className="h-5 w-5 text-slate-500" />;
    }
  };

  const getTransactionColor = (type, amount) => {
    if (amount > 0) return 'text-green-600';
    if (amount < 0) return 'text-red-600';
    return 'text-slate-600';
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-20">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-900 to-slate-800 text-white p-6">
        <div className="container mx-auto">
          <button
            data-testid="back-button"
            onClick={() => navigate('/dashboard')}
            className="text-white hover:text-orange-500 mb-4 flex items-center gap-2"
          >
            <ArrowLeft className="h-6 w-6" />
            Volver
          </button>
          <h1 className="text-2xl font-bold" data-testid="page-title">MI BILLETERA</h1>
        </div>
      </div>

      {/* Wallet Card */}
      <div className="container mx-auto px-4 -mt-8">
        <Card 
          className="bg-gradient-to-br from-slate-900 to-slate-800 text-white border-0 shadow-2xl overflow-hidden"
          data-testid="wallet-card"
        >
          <div className="absolute top-0 right-0 w-64 h-64 bg-orange-500/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-orange-500/10 rounded-full blur-3xl"></div>
          
          <CardContent className="relative p-8">
            <div className="flex items-center gap-2 mb-2">
              <WalletIcon className="h-6 w-6 text-orange-500" />
              <span className="text-slate-300 font-medium">Saldo Disponible</span>
            </div>
            <h2 className="text-5xl font-bold mb-6" data-testid="wallet-balance">
              ${wallet.balance?.toLocaleString() || '0'}
              <span className="text-2xl text-slate-400 ml-2">COP</span>
            </h2>
            
            <Dialog open={showRechargeDialog} onOpenChange={setShowRechargeDialog}>
              <DialogTrigger asChild>
                <Button
                  data-testid="recharge-button"
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold py-6 rounded-full"
                >
                  <Plus className="mr-2 h-5 w-5" /> Recargar Saldo
                </Button>
              </DialogTrigger>
              <DialogContent data-testid="recharge-dialog">
                <DialogHeader>
                  <DialogTitle>Recargar Billetera</DialogTitle>
                  <DialogDescription>
                    Ingresa el monto que deseas agregar a tu billetera
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="amount">Monto (COP)</Label>
                    <Input
                      id="amount"
                      data-testid="recharge-amount-input"
                      type="number"
                      placeholder="50000"
                      value={rechargeAmount}
                      onChange={(e) => setRechargeAmount(e.target.value)}
                    />
                  </div>
                  
                  <div className="grid grid-cols-3 gap-2">
                    {[50000, 100000, 200000].map(amount => (
                      <Button
                        key={amount}
                        data-testid={`quick-amount-${amount}`}
                        variant="outline"
                        onClick={() => setRechargeAmount(amount.toString())}
                        className="border-orange-200 hover:border-orange-500"
                      >
                        ${(amount / 1000).toFixed(0)}k
                      </Button>
                    ))}
                  </div>

                  <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                    <div className="flex items-start gap-2">
                      <CreditCard className="h-5 w-5 text-slate-500 mt-0.5" />
                      <div>
                        <p className="text-sm font-semibold text-slate-700">Método de Pago</p>
                        <p className="text-xs text-slate-500">PSE, Tarjeta de Crédito/Débito</p>
                        <p className="text-xs text-orange-600 mt-1">Demo: La recarga se simulará instantáneamente</p>
                      </div>
                    </div>
                  </div>

                  <Button
                    data-testid="confirm-recharge-button"
                    onClick={handleRecharge}
                    className="w-full bg-orange-500 hover:bg-orange-600 text-white"
                  >
                    Confirmar Recarga
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </CardContent>
        </Card>

        {/* Transactions */}
        <Card className="mt-6" data-testid="transactions-card">
          <CardHeader>
            <CardTitle>Historial de Transacciones</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-center text-slate-500">Cargando...</p>
            ) : transactions.length === 0 ? (
              <p className="text-center text-slate-500 py-8">No hay transacciones aún</p>
            ) : (
              <div className="space-y-3">
                {transactions.map((transaction) => (
                  <div
                    key={transaction.id}
                    data-testid={`transaction-${transaction.id}`}
                    className="flex items-center justify-between p-3 border border-slate-200 rounded-lg hover:bg-slate-50"
                  >
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-slate-100 rounded-full">
                        {getTransactionIcon(transaction.type)}
                      </div>
                      <div>
                        <p className="font-semibold text-slate-900">{transaction.description}</p>
                        <p className="text-xs text-slate-500">
                          {new Date(transaction.timestamp).toLocaleString()}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`font-bold text-lg ${getTransactionColor(transaction.type, transaction.amount)}`}>
                        {transaction.amount > 0 ? '+' : ''}
                        ${Math.abs(transaction.amount).toLocaleString()}
                      </p>
                      <p className="text-xs text-slate-500">
                        Saldo: ${transaction.balance_after?.toLocaleString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}